/**
 * Unified WebSocket Interface
 *
 * This file provides a standardized interface for WebSocket functionality
 * that wraps the main WebSocket service.
 *
 * NOTE: The legacy Zustand WebSocket store (/stores/websocket.ts) has been
 * removed. This adapter remains as the compatibility layer for any code that
 * previously depended on the deprecated hooks.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useWebSocket } from './websocket'

// Unified WebSocket message interface
export interface UnifiedWebSocketMessage {
  type: string
  data: any
  sessionId?: string
  agentId?: number
  timestamp?: string
}

// Unified WebSocket state interface
export interface UnifiedWebSocketState {
  isConnected: boolean
  isConnecting: boolean
  connectionId: string | null
  lastError: string | null
  isRegistered: boolean
  registeredAs: 'user' | 'agent' | null
}

// Unified WebSocket callbacks
export interface UnifiedWebSocketCallbacks {
  onMessage?: (message: UnifiedWebSocketMessage) => void
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Event | string) => void
}

/**
 * Unified WebSocket hook that uses the preferred main WebSocket service
 * This provides the same interface as the deprecated Zustand store hooks
 * but uses the more robust main WebSocket implementation.
 */
export function useUnifiedWebSocket(
  url?: string,
  callbacks: UnifiedWebSocketCallbacks = {}
) {
  const [state, setState] = useState<UnifiedWebSocketState>({
    isConnected: false,
    isConnecting: false,
    connectionId: null,
    lastError: null,
    isRegistered: false,
    registeredAs: null,
  })

  // Use a ref to always have access to the latest callbacks
  // This prevents stale closure issues when WebSocket messages arrive
  const callbacksRef = useRef<UnifiedWebSocketCallbacks>(callbacks)
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  // Use the main WebSocket service
  const {
    isConnected,
    connectionId,
    sendMessage,
    disconnect,
    registerAgent,
    registerUser,
  } = useWebSocket(url || '', {
    onConnect: () => {
      setState(prev => ({ ...prev, isConnected: true, isConnecting: false }))
      callbacksRef.current.onConnect?.()
    },
    onDisconnect: () => {
      setState(prev => ({ ...prev, isConnected: false, isRegistered: false }))
      callbacksRef.current.onDisconnect?.()
    },
    onError: error => {
      setState(prev => ({
        ...prev,
        lastError: error instanceof Event ? 'Connection error' : error,
        isConnecting: false,
      }))
      callbacksRef.current.onError?.(error)
    },
    onMessage: message => {
      callbacksRef.current.onMessage?.(message as UnifiedWebSocketMessage)

      // Handle registration confirmation
      if (message.type === 'registration_confirmed') {
        const data = message.data as any // Allow any type for registration data
        setState(prev => ({
          ...prev,
          isRegistered: true,
          registeredAs: data?.connectionType || null,
          connectionId: data?.connectionId || null,
        }))
      }
    },
  })

  // Sync derived connection state fields with the underlying service
  useEffect(() => {
    setState(prev => ({ ...prev, isConnected }))
  }, [isConnected])

  useEffect(() => {
    setState(prev => ({ ...prev, connectionId }))
  }, [connectionId])

  const register = useCallback(
    (
      type: 'user' | 'agent',
      metadata: { sessionId?: string; agentId?: number }
    ) => {
      if (type === 'agent' && metadata.agentId) {
        registerAgent(metadata.agentId, metadata.sessionId)
      } else if (type === 'user' && metadata.sessionId) {
        registerUser(metadata.sessionId)
      }

      setState(prev => ({ ...prev, registeredAs: type }))
    },
    [registerAgent, registerUser]
  )

  const send = useCallback(
    (message: UnifiedWebSocketMessage) => {
      sendMessage(message)
    },
    [sendMessage]
  )

  return {
    ...state,
    register,
    sendMessage: send,
    disconnect,
  }
}

/**
 * Unified message subscription hook
 * Replaces useWebSocketMessage from the deprecated Zustand store
 */
export function useUnifiedWebSocketMessage(
  messageType: string,
  handler: (data: any) => void,
  deps: any[] = []
) {
  // Use a ref to always have access to the latest handler
  // This prevents stale closure issues
  const handlerRef = useRef(handler)
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  const messageHandler = useCallback(
    (message: UnifiedWebSocketMessage) => {
      if (message.type === messageType) {
        handlerRef.current(message.data)
      }
    },
    [messageType] // Only depend on messageType, not handler
  )

  useUnifiedWebSocket(undefined, { onMessage: messageHandler })
}

/**
 * Unified connection management hook
 * Replaces useWebSocketConnection from the deprecated Zustand store
 */
export function useUnifiedWebSocketConnection(
  autoConnect = true,
  registrationType?: 'user' | 'agent',
  registrationData?: { sessionId?: string; agentId?: number }
) {
  const [isReady, setIsReady] = useState(false)
  const [connectionEstablished, setConnectionEstablished] = useState(false)
  const registrationAttempted = useRef(false)

  const webSocket = useUnifiedWebSocket(undefined, {
    onConnect: () => {
      setIsReady(true)
      // Reset registration flag on new connection
      registrationAttempted.current = false
      setConnectionEstablished(false)
    },
    onMessage: message => {
      // Wait for connection_established event from backend before registering
      if (message.type === 'connection_established') {
        setConnectionEstablished(true)
      }
    },
  })

  // Auto-register when connection is established and we haven't registered yet
  useEffect(() => {
    if (
      connectionEstablished &&
      !registrationAttempted.current &&
      registrationType &&
      registrationData
    ) {
      registrationAttempted.current = true
      webSocket.register(registrationType, registrationData)
    }
  }, [connectionEstablished, registrationType, registrationData, webSocket])

  return {
    ...webSocket,
    isReady,
  }
}

/**
 * Migration helper: provides compatibility with old Zustand store interface
 * This allows existing components to work without immediate changes
 *
 * @deprecated Use useUnifiedWebSocket, useUnifiedWebSocketMessage, or useUnifiedWebSocketConnection instead
 */
export const CompatibilityLayer = {
  useWebSocketStore: (selector: any) => {
    console.warn(
      'useWebSocketStore is deprecated. Please migrate to useUnifiedWebSocket.'
    )
    // Return a minimal compatible object
    return {
      isConnected: false,
      sendMessage: () => false,
      disconnect: () => {},
      register: () => {},
    }
  },
  useWebSocketMessage: useUnifiedWebSocketMessage,
  useWebSocketConnection: useUnifiedWebSocketConnection,
}

const UnifiedWebSocket = {
  useUnifiedWebSocket,
  useUnifiedWebSocketMessage,
  useUnifiedWebSocketConnection,
  CompatibilityLayer,
}

export default UnifiedWebSocket
